import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import axiosInstance from './axios';
import { getConfig } from './config';
import { getQueryClient } from './query-client';
import { APIResponse, User, UserProfile } from './types';
import { ProtoUser, UserSummary } from '~/modules/staff';
import { Override } from '~/modules/@shared/types/utils.ts';

type UserProfilePayload = Override<
  UserProfile,
  {
    role_type_id: number;
    birth_date?: string | null;
    start_date?: string | null;
  }
>;

const getUsers = ({
  locationId,
  query,
}: {
  locationId: number;
  query?: {
    roles: number[];
  };
}) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<UserSummary[]>>(`${config.domains.auth}/admin/locations/${locationId}/users`, {
      params: query,
      paramsSerializer: {
        indexes: false, // null,
      },
    })
    .then(({ data }) => data?.data);
};

export const useGetUsersQuery = (params: {
  locationId: number;
  query?: {
    roles: number[];
  };
}) =>
  useQuery({
    queryKey: ['users', params],
    queryFn: () => getUsers(params),
    select(data) {
      if (!Array.isArray(data)) {
        return [];
      }
      return data;
    },
  });

const getUser = async ({ locationId, userId }: { locationId: number; userId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<User>>(`${config.domains.auth}/admin/locations/${locationId}/users/${userId}`)
    .then(({ data }) => data?.data);
};

export const useGetUserQuery: any = (params: { locationId: number; userId: number }) =>
  useQuery({
    queryKey: ['user', params],
    queryFn: () => getUser(params),
    enabled: !!params.userId && !isNaN(params.userId),
  });

const createUser = ({ locationId }: { locationId: number }, staff: ProtoUser) => {
  const config = getConfig();

  return axiosInstance.post<APIResponse>(`${config.domains.auth}/admin/locations/${locationId}/users`, staff);
};
export const useCreateUserMutation: any = (params: { locationId: number }) =>
  useMutation({
    mutationFn: (staff: ProtoUser) => createUser(params, staff),
    onSuccess: async (response) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['users', params] });
      toast.success(response.data.message);
    },
  });

const getUserProfile = ({ locationId, userId }: { locationId: number; userId: number }) => {
  const config = getConfig();

  return axiosInstance
    .get<APIResponse<UserProfile>>(`${config.domains.auth}/admin/locations/${locationId}/users/${userId}/profile`)
    .then(({ data }) => data?.data);
};

export const useGetUserProfileQuery = (params: { locationId: number; userId: number }) =>
  useQuery({ queryKey: ['user', { ...params, type: 'profile' }], queryFn: () => getUserProfile(params) });

const updateUserProfile = (
  { locationId, userId }: { locationId: number; userId: number },
  staff: Partial<UserProfilePayload>,
) => {
  const config = getConfig();
  const { user_id, ...rest } = staff;

  return axiosInstance.put<APIResponse<UserProfilePayload>>(
    `${config.domains.auth}/admin/locations/${locationId}/users/${userId}/profile`,
    rest,
  );
};
export const useUpdateUserProfileMutation = (params: { locationId: number; userId: number }) =>
  useMutation({
    mutationFn: (staff: Partial<UserProfilePayload>) => updateUserProfile(params, staff),
    onSuccess: async (response) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['users', { locationId: params.locationId }] });
      await queryClient.refetchQueries({ queryKey: ['user', { ...params, type: 'profile' }] });
      await queryClient.refetchQueries({ queryKey: ['location', params.locationId, []] });
      await queryClient.refetchQueries({
        queryKey: ['location', params.locationId, params.userId],
      });
      toast.success(response.data.message);
    },
  });

const deleteUserProfile = ({ locationId, userId }: { locationId: number; userId: number }) => {
  const config = getConfig();

  return axiosInstance.delete<APIResponse>(
    `${config.domains.auth}/admin/locations/${locationId}/users/${userId}/profile`,
  );
};
export const useDeleteUserProfileMutation: any = (params: { locationId: number; userId: number }) =>
  useMutation({
    mutationFn: () => deleteUserProfile(params),
    onSuccess: async (response) => {
      const queryClient = getQueryClient();

      await queryClient.refetchQueries({ queryKey: ['users', { locationId: params.locationId }] });
      await queryClient.refetchQueries({ queryKey: ['user', params] });
      await queryClient.refetchQueries({ queryKey: ['location', params.locationId, []] });
      await queryClient.refetchQueries({
        queryKey: ['location', params.locationId, params.userId],
      });
      toast.success(response.data.message);
    },
  });
